/// <reference types="google-maps" />

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { googleMapConfig } from 'Douleutaras/configuration/googleMap';
import { COUNTRY_CONFIG } from 'Douleutaras/constants/localization';
import { useLocalization } from 'Douleutaras/hooks/localization';

import { MapLoader } from './MapLoader';
import './LocationMap.scss';

// Delay after the user stops dragging before the map center is reverse-geocoded.
// Mirrors PIN_DELAY from dynamic-forms gis control (google-map.model.ts).
const PIN_DELAY = 1000;

const PIN_ICON = `${process.env.ASSETS_CDN_PATH}/douleutaras/img/douleutaras/pinplace.png`;

interface GoogleLocationMapProps {
  // Initial map center. When absent, the country's default center is used.
  center?: google.maps.LatLngLiteral;
  // When true, a newly provided `center` is reverse-geocoded and emitted through
  // `onChange`. Used when the center comes from the device location (which carries
  // no address yet) so it can be validated immediately.
  resolveCenter?: boolean;
  onChange(gis: Gis): void;
}

/**
 * Interactive map where a pin is locked to the map center. The user drags the map
 * underneath the pin; once dragging stops, the map center is reverse-geocoded and
 * the resulting address is emitted through `onChange`. This replicates the
 * "enableCenterPinLogic" behaviour of the dynamic-forms GIS control.
 */
export const GoogleLocationMap = ({ center, resolveCenter, onChange }: GoogleLocationMapProps) => {
  const { t } = useTranslation();
  const { countryCode } = useLocalization();
  const { isLoaded } = useJsApiLoader(googleMapConfig);

  const mapRef = useRef<google.maps.Map | undefined>(undefined);
  const pinRef = useRef<google.maps.Marker | undefined>(undefined);
  const geocoderRef = useRef<google.maps.Geocoder | undefined>(undefined);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Guards against reverse-geocoding the same device-location center more than once
  // (the map load and the center effect can both race to resolve it).
  const resolveGuardRef = useRef(false);
  // The move hint is shown until the user first interacts with (drags) the map.
  const [ hasInteracted, setHasInteracted ] = useState(false);

  const mapOptions: google.maps.MapOptions = {
    center: center || COUNTRY_CONFIG.MAP_CENTER[countryCode],
    zoom: center ? 16 : 11,
    disableDefaultUI: true,
    zoomControl: true,
    draggable: true
  };

  const reverseGeocode = useCallback((location: google.maps.LatLng | google.maps.LatLngLiteral): void => {
    const geocoder = geocoderRef.current;
    if (!geocoder) {
      return;
    }
    geocoder.geocode({ location }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results[0]) {
        onChange(new Gis(results[0]));
      }
    });
  }, [onChange]);

  const onDragStart = useCallback((): void => {
    setHasInteracted(true);
  }, []);

  const onDragEnd = useCallback((): void => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const map = mapRef.current;
      if (map) {
        reverseGeocode(map.getCenter());
      }
    }, PIN_DELAY);
  }, [reverseGeocode]);

  const onLoad = useCallback((map: google.maps.Map): void => {
    mapRef.current = map;
    geocoderRef.current = new google.maps.Geocoder();
    // Create a pin and bind it to the map center so it always tracks the centre
    // of the viewport while the user drags the map.
    const pin = new google.maps.Marker({
      map,
      position: map.getCenter(),
      icon: PIN_ICON
    });
    pin.bindTo('position', map, 'center');
    pinRef.current = pin;

    // Device-location center supplied before the map finished loading: validate it now.
    if (center && resolveCenter && !resolveGuardRef.current) {
      resolveGuardRef.current = true;
      reverseGeocode(center);
    }
  }, [center, resolveCenter, reverseGeocode]);

  const onUnmount = useCallback((): void => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    pinRef.current?.setMap(undefined);
    pinRef.current = undefined;
    mapRef.current = undefined;
    geocoderRef.current = undefined;
  }, []);

  // Re-centre the map when a new center is provided from outside (e.g. after an
  // address is selected via the autocomplete input). When the center comes from the
  // device location, also reverse-geocode it so it gets validated immediately.
  useEffect(() => {
    if (!center || !mapRef.current) {
      return;
    }
    mapRef.current.panTo(center);
    mapRef.current.setZoom(16);

    if (resolveCenter && !resolveGuardRef.current) {
      resolveGuardRef.current = true;
      reverseGeocode(center);
    }
  }, [center, resolveCenter, reverseGeocode]);

  // Reset the resolve guard once the parent stops requesting an initial resolve, so
  // a later device-location seed (e.g. reopening the map) is validated again.
  useEffect(() => {
    if (!resolveCenter) {
      resolveGuardRef.current = false;
    }
  }, [resolveCenter]);

  return (
    <div styleName="location-map">
      <div styleName="location-map__inner">
        { isLoaded
          ? <>
              <GoogleMap
                options={mapOptions}
                mapContainerStyle={{ width: '100%', height: '100%' }}
                onLoad={onLoad}
                onUnmount={onUnmount}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
              />
              { !hasInteracted &&
                <div styleName="location-map__hint">
                  {t('T.POST-JOB.MAP.MOVE-HINT', 'Move the map to pinpoint the exact location you are looking for')}
                </div>
              }
            </>
          : <MapLoader />
        }
      </div>
    </div>
  );
};

export default GoogleLocationMap;
